'use client';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useRef, useState } from 'react';
import { twc } from 'react-twc';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import ChevronLeft from '@/icons/ChevronLeft';
import ChevronRight from '@/icons/ChevronRight';
import { SearchMinusIcon } from '@/icons/photobox/SearchMinus';
import SearchPlusIcon from '@/icons/photobox/SearchPlus';
import XIcon from '@/icons/XIcon';
import imgAltMaker from '@/utils/imgAltMaker';

const PhotoBox = ({
  imgUrls,
  defaultImgidx = 0,
  closeAction,
}: {
  imgUrls: string[];
  defaultImgidx: number;
  closeAction?: () => void;
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel();
  const [currentIndex, setCurrentIndex] = useState(defaultImgidx);
  const transformRefs = useRef<(any | null)[]>([]);
  const [isZoomed, setIsZoomed] = useState(false);

  const handlePrev = () => {
    if (emblaApi) emblaApi.scrollPrev();
  };

  const handleNext = () => {
    if (emblaApi) emblaApi.scrollNext();
  };

  const handleZoomIn = () => {
    const ref = transformRefs.current[currentIndex];
    if (ref) {
      ref.zoomIn();
      setIsZoomed(true);
    }
  };

  const handleZoomOut = () => {
    const ref = transformRefs.current[currentIndex];
    if (ref) {
      ref.zoomOut();
      setIsZoomed(false);
    }
  };

  const handleResetTransform = () => {
    const ref = transformRefs.current[currentIndex];
    if (ref) {
      ref.resetTransform();
      setIsZoomed(false);
    }
  };

  const handleToggleZoom = () => {
    if (isZoomed) {
      handleZoomOut();
    } else {
      handleZoomIn();
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeAction) {
      closeAction();
    }
  };

  useEffect(() => {
    handleResetTransform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.scrollTo(defaultImgidx, true);
    setCurrentIndex(defaultImgidx);

    const onSelect = () => {
      const newIndex = emblaApi.selectedScrollSnap();
      setCurrentIndex(newIndex);
      // 슬라이드 변경 시 줌 상태 초기화
      setIsZoomed(false);
    };

    emblaApi.on('select', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [defaultImgidx, emblaApi]);

  return (
    <div
      className="relative w-screen h-screen flex items-center justify-center"
      onClick={handleBackgroundClick}
    >
      {/* 좌측 상단: 페이지 인디케이터 */}
      <div className="absolute top-[28px] left-4 md:left-[28px] z-[70] text-cds-coolgray-30 font-medium text-sm md:text-base">
        {currentIndex + 1}/{imgUrls.length}
      </div>

      {/* 우측 상단: 핀치 아이콘 & X 버튼 */}
      {/* top-[28px] right-[16px] md:right-[28px] */}
      <div className="absolute top-[28px] right-4 md:right-[28px] z-[70] flex items-center gap-5">
        {/* 핀치/확대축소 버튼 */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleZoom();
          }}
          className="text-white/80 hover:text-white text-xs md:text-sm flex items-center gap-1.5 transition-all cursor-pointer"
          aria-label={isZoomed ? '축소' : '확대'}
          data-gtm-id="photo-box-toggle-zoom"
        >
          {isZoomed ? (
            // 축소 아이콘 (줌인 상태)
            <SearchMinusIcon className="size-[26px] md:size-[30px]" />
          ) : (
            // 확대 아이콘 (기본 상태)
            <SearchPlusIcon className="size-[26px] md:size-[30px]" />
          )}
        </button>

        {/* X 버튼 */}
        <button
          onClick={closeAction}
          className="size-8 md:size-10 flex items-center justify-center transition-all"
          aria-label="닫기"
          data-gtm-id="photo-box-close"
        >
          <XIcon className="text-white size-[26px] md:size-[30px]" />
        </button>
      </div>

      {/* 메인 이미지 영역 - 전체 화면 */}
      <div className="w-full h-full relative">
        <div ref={emblaRef} className="w-full h-full">
          <div className="embla__container flex h-full">
            {imgUrls.map((imgSrc, index) => {
              return (
                <div
                  key={`${imgAltMaker(imgSrc)}-${index}`}
                  className="embla__slide min-w-0 w-full flex-none h-full"
                >
                  <TransformWrapper
                    ref={(el) => {
                      transformRefs.current[index] = el;
                    }}
                    initialScale={1}
                    minScale={1}
                    maxScale={4}
                    centerOnInit
                    doubleClick={{ mode: 'toggle' }}
                    wheel={{ step: 0.2 }}
                    onTransformed={(ref) => {
                      // 현재 슬라이드만 줌 상태 업데이트
                      if (index === currentIndex) {
                        const scale = ref.state.scale;
                        setIsZoomed(scale > 1);
                      }
                    }}
                  >
                    {() => (
                      <>
                        <TransformComponent
                          wrapperClass="!w-full !h-full"
                          contentClass="!w-full !h-full flex items-center justify-center"
                        >
                          <FullScreenImg src={imgSrc} alt={imgAltMaker(imgSrc)} />
                        </TransformComponent>
                      </>
                    )}
                  </TransformWrapper>
                </div>
              );
            })}
          </div>
        </div>

        {/* 좌측 네비게이션 - 화면 왼쪽 중앙 */}
        {imgUrls.length > 1 && (
          <button
            className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 cursor-pointer size-10 md:size-12 z-[60] bg-white/90 hover:bg-white rounded-full flex justify-center items-center shadow-xl transition-all backdrop-blur-sm"
            onClick={handlePrev}
            data-gtm-id="photo-box-prev"
            aria-label="이전 이미지"
          >
            <ChevronLeft className="text-gray-900 size-6 md:size-7" />
          </button>
        )}

        {/* 우측 네비게이션 - 화면 오른쪽 중앙 (줌 컨트롤 왼쪽) */}
        {imgUrls.length > 1 && (
          <button
            className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 cursor-pointer size-10 md:size-12 z-[60] bg-white/90 hover:bg-white rounded-full flex justify-center items-center shadow-xl transition-all backdrop-blur-sm"
            onClick={handleNext}
            data-gtm-id="photo-box-next"
            aria-label="다음 이미지"
          >
            <ChevronRight className="text-gray-900 size-6 md:size-7" />
          </button>
        )}
      </div>
    </div>
  );
};

const FullScreenImg = twc.img`
  object-contain 
  w-full h-full
  md:w-auto md:h-full
  max-w-full max-h-full
`;

export default PhotoBox;
